import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useHistory } from "react-router";
import * as Yup from 'yup';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import { Tooltip } from '@mui/material';

import Input from '../../../components/Input';
import Button from '../../../components/Button';
import Upload from '../../../components/Upload';
import Select from '../../../components/Select';
import { ARABIC_REGEX, ENGLISH_REGEX, NAME_REGEX, ONE_SPACE_ONLY, ONLY_ENGLISH_SPACE_DOT_COMMA_SEMICOLON, ONLY_ARABIC_SPACE_DOT_COMMA_SEMICOLON } from '../../../utils/Patterns';
import { directories, generateUniqueFileName, uploadToS3 } from '../../../utils/Aws-S3';
import { setPageMainTitle } from '../../../utils/Helpers';
import { getProfileDetailsRequest, setupProfileRequest } from '../../../store/ProfileDetails/actions';
import { getCategoriesLookUpRequest } from '../../../store/LookUps/actions';
import messages from '../../../assets/locale/messages';
import { ROUTE_PATHS } from '../../../utils/RoutesPaths';

const EditProfile = () => {
  const lang = useSelector((state) => state.locale.lang);
  const { setupProfile, shared, settings } = messages[lang];
  const dispatch = useDispatch();
  const history = useHistory();

  const [imgBanner, setImgBanner] = useState();
  const [imgBannerChanged, setImgBannerChanged] = useState(false);
  const [imgLogo, setImgLogo] = useState();
  const [imgLogoChanged, setImgLogoChanged] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const categoryList = useSelector((state) => state.lookUps.categories);
  const profileDetails = useSelector((state) => state.profileDetails.myProfile);

  useEffect(() => {
    setPageMainTitle('SettingsManagment');
    getCategoriesDropDownList();
    getProfileDetails();
  }, []);

  useEffect(() => {
    setCategoryOptions(
      categoryList?.map((option) => ({
        id: option.id,
        label: lang === 'en' ? option.name_en : option.name_ar,
      }))
    );
  }, [categoryList, lang]);

  useEffect(() => {
    if (values['category']) {
      setFieldValue(
        'category',
        categoryOptions?.find((option) => option?.id === values['category'].id)
      );
    }
  }, [lang, categoryOptions]);

  useEffect(() => {
    if (Object.keys(profileDetails).length > 0) {
      setTouched({
        name_en: !!profileDetails?.name_en ?? false,
        name_ar: !!profileDetails?.name_ar ?? false,
        description_en: !!profileDetails?.description_en ?? false,
        description_ar: !!profileDetails?.description_ar ?? false,
        email: !!profileDetails?.email ?? false,
        category: !!categoryOptions?.find((option) => option?.id === profileDetails?.category?.id ?? false),
        banner_image: !!profileDetails?.banner_image ?? false,
        logo_image: !!profileDetails?.logo_image ?? false,
      });
      setValues({
        name_en: profileDetails?.name_en,
        name_ar: profileDetails?.name_ar,
        description_en: profileDetails?.description_en || '',
        description_ar: profileDetails?.description_ar || '',
        email: profileDetails?.email,
        banner_image: profileDetails?.banner_image,
        logo_image: profileDetails?.logo_image,
        category: categoryOptions?.find((option) => option?.id === profileDetails?.category?.id),
      });
    }
  }, [profileDetails]);

  const getCategoriesDropDownList = () => {
    dispatch(
      getCategoriesLookUpRequest({
        page: -1,
      })
    );
  };

  const getProfileDetails = () => {
    dispatch(getProfileDetailsRequest());
  };

  const {
    values,
    setFieldValue,
    handleSubmit,
    setValues,
    setTouched,
    touched,
    errors,
    setFieldTouched,
    dirty,
    isValid,
  } = useFormik({
    initialValues: {
      name_en: '',
      name_ar: '',
      description_en: '',
      description_ar: '',
      email: '',
      category: null,
      banner_image: null,
      logo_image: null,
    },
    validationSchema: Yup.object({
      name_en: Yup.string()
        .required('enNameRequired')
        .matches(NAME_REGEX, 'nameNotFollowREGEX')
        .matches(ENGLISH_REGEX, 'englishLettersOnly').max(100,"maximumChars100"),
      name_ar: Yup.string()
        .required('arNameRequired')
        .matches(NAME_REGEX, 'nameNotFollowREGEX')
        .matches(ARABIC_REGEX, 'arabicLettersOnly').max(100,"maximumChars100"),
      description_en: Yup.string().optional().matches(ONLY_ENGLISH_SPACE_DOT_COMMA_SEMICOLON,'onlyEnglishSpaceDotCommaSemicolon'),
      description_ar: Yup.string().optional().matches(ONLY_ARABIC_SPACE_DOT_COMMA_SEMICOLON,'onlyArabicSpaceDotCommaSemicolon'),
      category: Yup.object().nullable().required('categoryRequired'),
      banner_image: Yup.mixed().required('bannerRequired'),
      logo_image: Yup.mixed().required('logoRequired'),
      email: Yup.string(),
    }),
    onSubmit: ({ name_en, name_ar, description_en, description_ar, category, banner_image, logo_image }) => {
      let providerData = { name_en, name_ar, description_en, description_ar, category_id: category.id };
      CheckImageValidationAndUpload(providerData, banner_image, logo_image).then((NewproviderData) => {
        dispatch(setupProfileRequest({ 
          data: {...NewproviderData},
          from:'settings'
        }));
      });
    },
  });

  const CheckImageValidationAndUpload = async (providerData, banner_image, logo_image) => {
    if (banner_image) {
      if (imgBannerChanged) {
        const data = await uploadToS3(
          banner_image,
          generateUniqueFileName(banner_image.name),
          directories.providers_banners_spp
        );
        providerData.banner_image = data?.link;
      } else {
        providerData.banner_image = banner_image;
      }
    }
    if (logo_image) {
      if (imgLogoChanged) {
        const data = await uploadToS3(
          logo_image,
          generateUniqueFileName(logo_image.name),
          directories.providers_logos_spp
        );
        providerData.logo_image = data?.link;
      } else {
        providerData.logo_image = logo_image;
      }
    }
    return providerData;
  };

  return (
    <div className="setup-profile-page w-100">
      <div
        className="fsize-30 fweight-600 d-flex gap-1 text-primary mb-4" 
      >
        <span
          className="pointer"
          onClick={()=>{
            history.push(ROUTE_PATHS.settings)
          }}
        >
          { lang === 'en' ? 
                (<ArrowBackIosOutlinedIcon/>) : 
                (<ArrowForwardIosOutlinedIcon/>)
          }
        </span>
        <span  className="mt-1">{settings.editProfile}</span>
      </div>
      <form className="w-100" onSubmit={handleSubmit} noValidate>
        <div className="row mt-4 flex-column-reverse flex-md-row">
          <div className="col-12 my-4 mt-md-0">
            <span className="text-dark fsize-16 fweight-400 font-medium">
              {setupProfile.placeholders.uploadLogo}
              <span className="err-asterisk">*</span>
            </span>
            <Tooltip title={<span className="fsize-12">{shared.filesAccepted} </span>}>
              <span className="text-bg-primary-mid fsize-14 font-reguler ms-2">{shared.acceptedFormatLabel}</span>
            </Tooltip>
            <Upload
              id="logo_image"
              label={
                <div className="mt-2 pt-1 d-flex flex-column align-items-center">
                  <ImageOutlinedIcon fontSize="large" />
                  <span>{setupProfile.placeholders.uploadLogo}</span>
                </div>
              }
              onChange={(img, value) => {
                setImgLogo(img);
                setImgLogoChanged(true);
                setFieldValue('logo_image', value);
              }}
              img={imgLogo || values['logo_image']}
              name="logo_image"
              required={true}
              isInputHasErr={!!(touched['logo_image'] && errors['logo_image'])}
              errMsg={errors['logo_image']}
            />
          </div>

          <div className="col-12">
            <span className="text-dark fsize-16 fweight-400 font-medium">
              {setupProfile.placeholders.banner}
              <span className="err-asterisk">*</span>
            </span>
            <Tooltip title={<span className="fsize-12">{shared.filesAccepted} </span>}>
              <span className="text-bg-primary-mid fsize-14 font-reguler ms-2">{shared.acceptedFormatLabel}</span>
            </Tooltip>
            <Upload
              label={
                <div className="pt-1 d-flex flex-column align-items-center justify-content-center banner-image-upload-initial-container px-5">
                  <ImageOutlinedIcon fontSize="large" />
                  <span>{setupProfile.placeholders.banner}</span>
                </div>
              }
              onChange={(img, value) => {
                setImgBanner(img);
                setImgBannerChanged(true);
                setFieldValue('banner_image', value);
              }}
              img={imgBanner || values['banner_image']}
              name="image"
              isBanner={true}
              className="mt-3 min-height-image"
              bannerClassName={`banner-image-upload-container ${lang === 'ar' ? `image-postion-right` : ''}`}
              required={true}
            />
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-12">
            <Input
              id="name_en"
              label={shared.lists.enName}
              placeholder={setupProfile.placeholders.EnName}
              name="name_en"
              type="text"
              required
              onChange={(value) => {
                if (ONE_SPACE_ONLY.test(value) || !value) {
                  setFieldTouched('name_en');
                  setFieldValue('name_en', value);
                }
              }}
              value={values['name_en']}
              inputWrapperClass="mb-4"
              labelClassName="text-dark fsize-16 fweight-400 mb-2"
              isInputHasErr={!!(touched['name_en'] && errors['name_en'])}
              errMsg={errors['name_en']}
            />
          </div>
          <div className="col-12">
            <Input
              id="name_ar"
              label={shared.lists.arName}
              name="name_ar"
              type="text"
              placeholder={setupProfile.placeholders.ArName}
              required
              onChange={(value) => {
                if (ONE_SPACE_ONLY.test(value) || !value) {
                  setFieldTouched('name_ar');
                  setFieldValue('name_ar', value);
                }
              }}
              value={values['name_ar']}
              inputWrapperClass="mb-4"
              labelClassName="text-dark fsize-16 fweight-400 mb-2"
              isInputHasErr={!!(touched['name_ar'] && errors['name_ar'])}
              errMsg={errors['name_ar']}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <Input
              id="description_en"
              label={shared.lists.enDescription}
              placeholder={setupProfile.placeholders.enDescription}
              name="description_en"
              type="text"
              multiline
              inputProps={{ maxLength: 125 }}
              inputClass='p-14'
              onChange={(value) => {
                if (ONE_SPACE_ONLY.test(value) || !value) {
                  setFieldTouched('description_en');
                  setFieldValue('description_en', value);
                }
              }}
              value={values['description_en']}
              inputWrapperClass="mb-4"
              labelClassName="text-dark fsize-16 fweight-400 mb-2"
              isInputHasErr={!!(touched['description_en'] && errors['description_en'])}
              errMsg={errors['description_en']}
            />
          </div>
          <div className="col-12">
            <Input
              id="description_ar"
              label={shared.lists.arDescription}
              name="description_ar"
              type="text"
              placeholder={setupProfile.placeholders.arDescription}
              multiline
              inputProps={{ maxLength: 125 }}
              inputClass='p-14'
              onChange={(value) => {
                if (ONE_SPACE_ONLY.test(value) || !value) {
                  setFieldTouched('description_ar');
                  setFieldValue('description_ar', value);
                }
              }}
              value={values['description_ar']}
              inputWrapperClass="mb-4"
              labelClassName="text-dark fsize-16 fweight-400 mb-2"
              isInputHasErr={!!(touched['description_ar'] && errors['description_ar'])}
              errMsg={errors['description_ar']}
            />
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <Input
              id="email"
              label={shared.lists.email}
              placeholder={setupProfile.placeholders.email}
              name="email"
              type="text"
              required
              onChange={(value) => {
                setFieldTouched('email');
                setFieldValue('email', value);
              }}
              value={values['email']}
              inputWrapperClass="mb-4"
              labelClassName="text-dark fsize-16 fweight-400 mb-2"
              isInputHasErr={!!(touched['email'] && errors['email'])}
              errMsg={errors['email']}
              disabled={true}
            />
          </div>

          <div className="col-12">
            <Select
              name="category"
              labelClassName="text-dark fsize-16 fweight-400 mb-2"
              label={shared.lists.categories}
              options={categoryOptions}
              inputClassName="input-wrapper"
              placeholder={setupProfile.placeholders.category}
              required
              value={values['category']}
              onChange={(value) => {
                setFieldTouched('category');
                setFieldValue('category', value);
              }}
              hasError={!!(touched['category'] && errors['category'])}
              errMsg={errors['category']}
              disabled={profileDetails.branches_count}
            />
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <Button
            label={shared.cancel}
            labelClass="text-secondary px-3"
            outlined
            onClick={()=> {history.push(ROUTE_PATHS.settings)}}
          />
          <Button
            label={shared.save}
            type="submit"
            labelClass="text-white px-3"
            btnStyle="secondary"
            disabled={!dirty || !isValid}
          />
        </div>
        {/* <Button
          label={setupProfile.actionBTN}
          type="submit"
          labelClass="fsize-16 text-white py-1 fweight-600 border-radius-10"
          className="my-4"
          block
          disabled={!dirty || !isValid}
        /> */}
      </form>
    </div>
  );
};
export default EditProfile;
